<template>
  <div class="sg">
    <div class="sg-section-head">
      <h2>Presentation</h2>
      <p class="sg-help">
        Your practice "house style" — how treatment plans look to patients by default. Every new
        plan inherits these settings, so your team doesn't re-toggle them each time. A treatment
        coordinator can still override any of these per-patient while building or presenting a plan
        &mdash; except the disclaimer, which applies to every plan.
      </p>
    </div>

    <!-- Patient views -->
    <div class="sg-card">
      <h3 class="sg-card-title">Patient views</h3>
      <div class="sg-field">
        <label>Enabled patient views</label>
        <div class="sg-check-row">
          <label v-for="opt in viewOptions" :key="opt.id" class="sg-check">
            <input
              type="checkbox"
              :checked="isViewEnabled(opt.id)"
              :disabled="isViewEnabled(opt.id) && enabledViewOptions.length === 1"
              @change="onToggleEnabledView(opt.id, $event.target.checked)"
            />
            <span>{{ opt.label }}</span>
          </label>
        </div>
        <p class="sg-hint">
          Which layouts the patient can see. At least one must stay on — when only one is enabled,
          the patient sees no view switcher.
        </p>
      </div>
      <div class="sg-field" v-if="enabledViewOptions.length > 1">
        <label>Default layout</label>
        <div class="sg-radio-row">
          <label v-for="opt in enabledViewOptions" :key="opt.id" class="sg-radio">
            <input type="radio" :value="opt.id" v-model="local.defaultView" />
            <span>{{ opt.label }}</span>
          </label>
        </div>
        <p class="sg-hint">
          Which layout opens first. Patients and TCs can switch between enabled views any time.
        </p>
      </div>
    </div>

    <!-- Pricing display -->
    <div class="sg-card">
      <h3 class="sg-card-title">Pricing display</h3>

      <div class="sg-field">
        <label>Pricing shown by default</label>
        <div v-for="opt in viewOptions" :key="'pm-' + opt.id" class="sg-pricing-mode">
          <div class="sg-pricing-mode-view">{{ opt.label }}</div>
          <div class="sg-segmented">
            <button
              v-for="m in pricingModeOptions"
              :key="opt.id + '-' + m.id"
              type="button"
              class="sg-segmented-btn"
              :class="{ active: pricingModeFor(opt.id) === m.id }"
              :title="m.hint"
              @click="setPricingMode(opt.id, m.id)"
            >{{ m.label }}</button>
          </div>
        </div>
        <p class="sg-hint">
          <strong>Total only</strong> drops the per-procedure fees but keeps the Plan Summary total at
          the bottom — use it when a layout should open the conversation with the big picture.
          <strong>No pricing</strong> removes every dollar figure from that layout, total included.
          The TC can change this per-patient.
        </p>
      </div>

      <div class="sg-field">
        <label>Fee breakdown lines</label>
        <p class="sg-hint sg-hint-tight">
          Which sub-lines appear wherever a layout is set to <strong>Full pricing</strong>. These are
          not per-layout.
        </p>
        <div class="sg-toggle-list">
          <label v-for="layer in layerOptions" :key="layer.key" class="sg-switch-label sg-toggle-row">
            <span class="sg-switch">
              <input type="checkbox" v-model="local.displayDefaults.layers[layer.key]" />
              <span class="sg-switch-slider"></span>
            </span>
            <span class="sg-switch-text">
              <strong>{{ layer.label }}</strong>
              <span class="sg-switch-sub">{{ layer.sub }}</span>
            </span>
          </label>
        </div>
      </div>

      <div class="sg-field">
        <label>Plan Summary</label>
        <label class="sg-switch-label">
          <span class="sg-switch">
            <input type="checkbox" v-model="local.displayDefaults.showBalance" />
            <span class="sg-switch-slider"></span>
          </span>
          <span class="sg-switch-text">
            <strong>Show balance/credits from Open Dental</strong>
            <span class="sg-switch-sub">
              Adds a "Remaining Balance / Credits" line to the Plan Summary from the guarantor's
              Open Dental account balance (BalTotal − InsEst), folded into the Estimated Patient
              Portion. The TC can flip this on/off per-patient.
            </span>
          </span>
        </label>
      </div>

      <div class="sg-field">
        <label>Photos</label>
        <label class="sg-switch-label">
          <span class="sg-switch">
            <input type="checkbox" v-model="local.displayDefaults.showPhotos" />
            <span class="sg-switch-slider"></span>
          </span>
          <span class="sg-switch-text">
            <strong>Show attached photos in the treatment plan</strong>
            <span class="sg-switch-sub">
              Shows a camera icon beside any tooth that has a photo, plus a "Photos of Planned
              Teeth" button. Photos are attached to the <em>patient</em>, so every plan for that
              patient can show them &mdash; a photo appears on a plan when one of its tagged teeth
              is on that plan, and photos with no teeth tagged always appear. The TC can flip this
              on/off per-patient.
            </span>
          </span>
        </label>
      </div>
    </div>

    <!-- Itemized layout -->
    <div class="sg-card">
      <h3 class="sg-card-title">Itemized layout</h3>
      <label class="sg-switch-label">
        <span class="sg-switch">
          <input type="checkbox" v-model="local.displayDefaults.itemizedGroupByPriority" />
          <span class="sg-switch-slider"></span>
        </span>
        <span class="sg-switch-text">
          <strong>Group by priority</strong>
          <span class="sg-switch-sub">
            Break the Itemized view into priority groups (from Open Dental treatment priority), each
            with its own card and subtotal — like a phased treatment journey.
          </span>
        </span>
      </label>

      <div class="sg-sub" :class="{ 'sg-field-muted': !local.displayDefaults.itemizedGroupByPriority }">
        <div class="sg-field">
          <label>Group header word</label>
          <div class="sg-radio-row">
            <label v-for="word in groupLabelPresets" :key="word" class="sg-radio">
              <input type="radio" :value="word" :checked="groupLabel === word" @change="setGroupLabel(word)" />
              <span>{{ word }}</span>
            </label>
            <label class="sg-radio sg-radio-custom" :class="{ 'sg-radio-active': isCustomLabel }">
              <input type="radio" :checked="isCustomLabel" @change="setGroupLabel(customLabelDraft || 'Group')" />
              <span>Custom:</span>
              <input
                class="sg-mini-input"
                type="text"
                v-model="customLabelDraft"
                placeholder="e.g., Step"
                @input="onCustomLabelInput"
                @focus="setGroupLabel(customLabelDraft || 'Group')"
              />
            </label>
          </div>
          <p class="sg-hint">
            Each group is labeled "{{ groupLabel }} 1", "{{ groupLabel }} 2", … Unprioritized
            procedures fall under "Additional".
          </p>
        </div>

        <label class="sg-switch-label sg-toggle-row">
          <span class="sg-switch">
            <input type="checkbox" v-model="local.displayDefaults.itemizedGroupSubtotals" />
            <span class="sg-switch-slider"></span>
          </span>
          <span class="sg-switch-text">
            <strong>Show per-group subtotals</strong>
            <span class="sg-switch-sub">Display a Total Fee line at the bottom of each priority group.</span>
          </span>
        </label>
      </div>
    </div>

    <!-- Procedures -->
    <div class="sg-card">
      <h3 class="sg-card-title">Procedures</h3>
      <label class="sg-switch-label">
        <span class="sg-switch">
          <input type="checkbox" v-model="local.displayDefaults.showDiagnostic" />
          <span class="sg-switch-slider"></span>
        </span>
        <span class="sg-switch-text">
          <strong>Show exams &amp; X-rays by default</strong>
          <span class="sg-switch-sub">
            Include diagnostic procedures (D0xxx — exams, X-rays) in the plan. Preventive items
            (cleanings, fluoride) always show regardless of this setting.
          </span>
        </span>
      </label>
    </div>

    <!-- Disclaimer & acknowledgements. PRACTICE-WIDE (no per-plan override). Default
         OFF: no lines + paragraph hidden = every surface behaves as it always did.
         Rules / caps / what a patient sees: utils/disclaimer.js. -->
    <div class="sg-card" data-test="txs-disclaimer-card">
      <h3 class="sg-card-title">Disclaimer &amp; acknowledgements</h3>
      <p class="sg-hint sg-hint-tight">
        Shown above the signature on every plan this office presents &mdash; chairside and on the
        patient's link &mdash; and printed on the signed PDF. Applies to every plan (no per-plan
        override).
      </p>

      <div class="sg-field">
        <label>Acknowledgement lines</label>
        <div v-if="local.disclaimer.acknowledgements.length === 0" class="sg-empty">
          No acknowledgement lines yet. Patients see Teamio's built-in confirmation checkbox.
        </div>
        <div
          v-for="(ack, idx) in local.disclaimer.acknowledgements"
          :key="ack.id"
          class="sg-ack-row"
          data-test="txs-ack-row"
        >
          <input
            v-model="ack.text"
            type="text"
            class="sg-input sg-ack-text"
            :maxlength="disclaimerLimits.LINE_MAX"
            placeholder="I understand that any treatment plan presented to me is an estimate."
            data-test="txs-ack-text"
          />
          <label class="sg-switch-label sg-ack-required">
            <span class="sg-switch">
              <input type="checkbox" v-model="ack.required" data-test="txs-ack-required" />
              <span class="sg-switch-slider"></span>
            </span>
            <span class="sg-ack-required-text">Required to sign</span>
          </label>
          <button
            type="button"
            class="sg-icon-btn sg-icon-btn-danger"
            title="Remove line"
            data-test="txs-ack-remove"
            @click="onRemoveAck(idx)"
          >
            <i class="fa fa-trash"></i>
          </button>
        </div>
        <button
          v-if="canAddAck"
          type="button"
          class="sg-btn-mini"
          data-test="txs-ack-add"
          @click="onAddAck"
        >
          <i class="fa fa-plus"></i> Add line
        </button>
        <p class="sg-hint">
          Each line is a checkbox the patient ticks before signing. Required lines block the Sign
          button until checked. When you add your own lines, Teamio's built-in "I confirm I've
          reviewed&hellip;" checkbox is replaced by yours.
        </p>
      </div>

      <div class="sg-field">
        <label>Disclaimer text</label>
        <textarea
          v-model="local.disclaimer.text"
          class="sg-input sg-textarea"
          rows="8"
          :maxlength="disclaimerLimits.TEXT_MAX"
          placeholder="e.g. A benefits quote from your insurance is an estimate, not a guarantee of payment…"
          data-test="txs-disclaimer-text"
        ></textarea>
        <label class="sg-switch-label sg-disclaimer-show">
          <span class="sg-switch">
            <input type="checkbox" v-model="local.disclaimer.showText" data-test="txs-disclaimer-show" />
            <span class="sg-switch-slider"></span>
          </span>
          <span class="sg-switch-text">
            <strong>Show on treatment plans above the signature</strong>
          </span>
        </label>
        <p class="sg-hint">
          Plain text; line breaks are kept. Printed on the signed PDF exactly as typed.
        </p>
      </div>
    </div>

    <!-- Footer: save bar -->
    <div class="sg-savebar">
      <span v-if="dirty" class="sg-dirty">Unsaved changes</span>
      <span v-else class="sg-clean">All changes saved</span>
      <span class="sg-spacer"></span>
      <button class="sg-btn" @click="onReset" :disabled="!dirty">Reset</button>
      <button class="sg-btn-primary" @click="onSave" :disabled="!dirty || saving">
        <i v-if="saving" class="fa fa-circle-o-notch fa-spin"></i>
        Save
      </button>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import mixin from '@/mixin.js';
import { TX_PRESENTER_DEFAULTS } from '@/pages/TxPresenter/constants/settingsDefaults.js';
import { TX_PRICING_MODE_OPTIONS } from '@/pages/TxPresenter/constants/views.js';
import { buildPricingModePatch } from '@/pages/TxPresenter/utils/resolveDisplay.js';
import { DISCLAIMER_LIMITS } from '@/pages/TxPresenter/utils/disclaimer.js';

const cloneDefaults = () => JSON.parse(JSON.stringify(TX_PRESENTER_DEFAULTS.presentation));

// Makes two lines added within the same millisecond (a fast double tap on "Add line")
// still get distinct ids.
let ackSeq = 0;

export default {
  name: 'SettingsPresentation',
  mixins: [mixin],
  props: {
    loading: { type: Boolean, default: false },
    saving: { type: Boolean, default: false },
  },
  data() {
    return {
      local: cloneDefaults(),
      original: cloneDefaults(),
      viewOptions: [
        { id: 'region', label: 'Arch & Region' },
        { id: 'itemized', label: 'Itemized' },
      ],
      layerOptions: [
        { key: 'netFee', label: 'Net fee (PPO / office-collectable)', sub: 'The primary number the TC walks the patient through.' },
        { key: 'standardFee', label: 'Standard fee (UCR)', sub: 'Sub-line shown when the contracted rate differs from UCR.' },
        { key: 'insuranceEstimate', label: 'Insurance estimate', sub: 'Estimated insurance contribution per procedure.' },
        { key: 'membership', label: 'Show Procedure Discounts', sub: 'Shows Open Dental procedure discounts as an OD-style pre-discount fee with a Discount line (visible to the patient when on).' },
      ],
      // Shared with the Builder Financials tab and the chairside tray so the
      // wording is identical wherever a mode is chosen.
      pricingModeOptions: TX_PRICING_MODE_OPTIONS,
      groupLabelPresets: ['Priority', 'Visit', 'Phase'],
      customLabelDraft: '',
      disclaimerLimits: DISCLAIMER_LIMITS,
    };
  },
  computed: {
    ...mapGetters('txPresenter', ['settingsPresentationResolved']),
    dirty() {
      return JSON.stringify(this.local) !== JSON.stringify(this.original);
    },
    canAddAck() {
      return this.local.disclaimer.acknowledgements.length < DISCLAIMER_LIMITS.MAX_LINES;
    },
    enabledViewOptions() {
      return this.viewOptions.filter((o) => this.isViewEnabled(o.id));
    },
    groupLabel() {
      return (this.local.displayDefaults.itemizedGroupLabel || 'Priority').trim() || 'Priority';
    },
    isCustomLabel() {
      return !this.groupLabelPresets.includes(this.groupLabel);
    },
  },
  watch: {
    settingsPresentationResolved: {
      immediate: true,
      handler(resolved) {
        if (!resolved) return;
        const base = cloneDefaults();
        const merged = {
          enabledViews: { ...base.enabledViews, ...(resolved.enabledViews || {}) },
          defaultView: resolved.defaultView || base.defaultView,
          displayDefaults: {
            ...base.displayDefaults,
            ...(resolved.displayDefaults || {}),
            viewVisibility: { ...base.displayDefaults.viewVisibility, ...((resolved.displayDefaults || {}).viewVisibility || {}) },
            layers: { ...base.displayDefaults.layers, ...((resolved.displayDefaults || {}).layers || {}) },
          },
          // ⛔ This watcher REBUILDS the blob from named keys: a key not carried here is
          //    dropped on read and erased from the saved blob on the next Save.
          //    Deep-cloned so editing a row never mutates the store's resolved copy.
          disclaimer: JSON.parse(JSON.stringify(resolved.disclaimer || base.disclaimer)),
        };
        if (merged.defaultView === 'phase') merged.defaultView = 'region';
        this.local = merged;
        this.original = JSON.parse(JSON.stringify(merged));
        // Seed the custom-label input when the saved label isn't a preset.
        if (!this.groupLabelPresets.includes((merged.displayDefaults.itemizedGroupLabel || '').trim())) {
          this.customLabelDraft = (merged.displayDefaults.itemizedGroupLabel || '').trim();
        }
      },
    },
  },
  methods: {
    /**
     * The practice default for one view, falling back to the legacy
     * viewVisibility boolean so a practice that set "Show prices by default in"
     * before this feature keeps its choice.
     */
    pricingModeFor(view) {
      const dd = this.local.displayDefaults || {};
      const mode = (dd.pricing || {})[view];
      if (mode === 'full' || mode === 'total' || mode === 'none') return mode;
      return (dd.viewVisibility || {})[view] === false ? 'none' : 'full';
    },

    /**
     * Writes both the mode and the legacy boolean via the shared helper, so a
     * practice default resolves the same way a per-patient one does.
     */
    setPricingMode(view, mode) {
      const dd = this.local.displayDefaults || {};
      const patch = buildPricingModePatch(dd, view, mode);
      this.$set(dd, 'pricing', patch.pricing);
      this.$set(dd, 'viewVisibility', patch.viewVisibility);
    },

    isViewEnabled(id) {
      const ev = this.local.enabledViews || {};
      return ev[id] !== false;
    },
    onToggleEnabledView(id, checked) {
      const next = {
        region: this.isViewEnabled('region'),
        itemized: this.isViewEnabled('itemized'),
      };
      next[id] = checked;
      // Never allow zero enabled views (the disabled attr on the last checkbox
      // should prevent this, but keep the safety net).
      if (!next.region && !next.itemized) {
        this.$forceUpdate();
        return;
      }
      this.$set(this.local, 'enabledViews', next);
      // Keep defaultView pointing at an enabled view.
      if (!next[this.local.defaultView]) {
        this.local.defaultView = next.region ? 'region' : 'itemized';
      }
    },
    setGroupLabel(word) {
      const w = (word == null ? '' : String(word)).trim();
      this.$set(this.local.displayDefaults, 'itemizedGroupLabel', w || 'Priority');
    },
    onCustomLabelInput() {
      const w = (this.customLabelDraft || '').trim();
      this.setGroupLabel(w || 'Group');
    },
    /**
     * Append a blank, REQUIRED acknowledgement line (the safe default: it blocks
     * Sign until ticked). Blank lines are dropped by the server's normalize on save.
     */
    onAddAck() {
      const lines = this.local.disclaimer.acknowledgements;
      if (lines.length >= DISCLAIMER_LIMITS.MAX_LINES) return;
      const used = new Set(lines.map((a) => a.id));
      let id;
      do { id = 'ack_' + Date.now() + '_' + (++ackSeq); } while (used.has(id));
      lines.push({ id, text: '', required: true });
    },
    onRemoveAck(idx) {
      const lines = this.local.disclaimer.acknowledgements;
      const row = lines[idx];
      if (!row) return;
      // Only ask when there is something to lose - an empty row just goes away.
      if (String(row.text || '').trim() !== '' && !window.confirm('Remove this acknowledgement line?')) return;
      lines.splice(idx, 1);
    },
    async onSave() {
      try {
        await this.$store.dispatch('txPresenter/saveSettingsSection', {
          section: 'presentation',
          value: this.local,
        });
        this.original = JSON.parse(JSON.stringify(this.local));
      } catch (e) { /* in state */ }
    },
    onReset() {
      this.local = JSON.parse(JSON.stringify(this.original));
      const lbl = (this.local.displayDefaults.itemizedGroupLabel || '').trim();
      this.customLabelDraft = this.groupLabelPresets.includes(lbl) ? '' : lbl;
    },
  },
};
</script>

<style scoped>
.sg {
  max-width: 720px;
  margin: 0 auto;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, system-ui, sans-serif;
}
.sg-section-head { margin-bottom: 22px; }
.sg-section-head h2 {
  margin: 0 0 4px;
  font-size: 22px;
  font-weight: 600;
  letter-spacing: -0.3px;
  color: #0f172a;
}
.sg-help {
  margin: 0;
  font-size: 13px;
  color: #64748b;
  line-height: 1.55;
}

.sg-card {
  background: #fff;
  border: 1px solid #e4e7ec;
  border-radius: 10px;
  padding: 20px 22px;
  margin-bottom: 16px;
  box-shadow: 0 1px 2px rgba(15,23,42,0.04);
}
.sg-card-title {
  margin: 0 0 14px;
  font-size: 13px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 1.2px;
  color: #475569;
}

.sg-field { margin-bottom: 16px; }
.sg-field:last-child { margin-bottom: 0; }
.sg-field > label {
  display: block;
  font-size: 12px;
  color: #475569;
  font-weight: 600;
  margin-bottom: 6px;
}

.sg-hint {
  margin: 6px 0 0;
  font-size: 11.5px;
  color: #94a3b8;
  line-height: 1.5;
}
/* Hint that INTRODUCES the controls below it rather than following them. */
.sg-hint-tight { margin: -2px 0 8px; }

/* ── Disclaimer & acknowledgements ── */
.sg-input {
  display: block;
  width: 100%;
  padding: 8px 10px;
  font-size: 13px;
  border: 1px solid #e4e7ec;
  border-radius: 6px;
  background: #fff;
  color: #0f172a;
  font-family: inherit;
}
.sg-input:focus {
  outline: none;
  border-color: #0d5f54;
  box-shadow: 0 0 0 3px rgba(13, 95, 84, 0.12);
}
.sg-textarea {
  resize: vertical;
  min-height: 100px;
  line-height: 1.55;
  font-family: inherit;
}
.sg-empty {
  padding: 16px;
  margin-bottom: 10px;
  text-align: center;
  color: #94a3b8;
  font-size: 13px;
  border: 1px dashed #e4e7ec;
  border-radius: 8px;
}
.sg-ack-row {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 8px;
}
.sg-ack-row .sg-ack-text { flex: 1; min-width: 0; }
.sg-ack-row .sg-ack-required { align-items: center; flex-shrink: 0; white-space: nowrap; }
.sg-ack-required-text { font-size: 12px; color: #475569; font-weight: 500; }
.sg-disclaimer-show { margin-top: 10px; }
.sg-icon-btn,
.sg-btn-mini {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 5px 10px;
  border-radius: 6px;
  background: #fff;
  border: 1px solid #e4e7ec;
  color: #475569;
  font-size: 12px;
  cursor: pointer;
}
.sg-icon-btn { padding: 5px 8px; flex-shrink: 0; }
.sg-icon-btn:hover { background: #f1f3f6; }
.sg-icon-btn-danger:hover { background: #fef2f2; color: #b91c1c; border-color: #fecaca; }
.sg-btn-mini:hover { background: #ecf5f3; color: #0d5f54; border-color: #b9dcd5; }
@media (max-width: 560px) {
  .sg-ack-row { flex-wrap: wrap; }
  .sg-ack-row .sg-ack-text { flex-basis: 100%; }
}

/* ── Per-view pricing mode ──
   Segmented so all three modes are visible at once. "Total only" is the option
   a client couldn't find because it didn't exist; burying it in a dropdown
   would repeat the mistake in a different way. */
.sg-pricing-mode + .sg-pricing-mode { margin-top: 10px; }
.sg-pricing-mode-view {
  font-size: 12.5px;
  color: #334155;
  margin-bottom: 5px;
}
.sg-segmented {
  display: flex;
  border: 1px solid #e4e7ec;
  border-radius: 8px;
  overflow: hidden;
  background: #ffffff;
  max-width: 420px;
}
.sg-segmented-btn {
  flex: 1;
  padding: 7px 6px;
  border: none;
  border-right: 1px solid #e4e7ec;
  background: transparent;
  color: #475569;
  font-size: 11.5px;
  font-weight: 500;
  line-height: 1.3;
  cursor: pointer;
  transition: background 0.12s, color 0.12s;
}
.sg-segmented-btn:last-child { border-right: none; }
.sg-segmented-btn:hover:not(.active) { background: #f1f3f6; }
.sg-segmented-btn.active {
  background: #0f6e63;
  color: #ffffff;
  font-weight: 600;
}

.sg-radio-row {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  align-items: center;
}
.sg-radio {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border: 1px solid #e4e7ec;
  border-radius: 999px;
  font-size: 12.5px;
  color: #1e293b;
  cursor: pointer;
  background: #fff;
}
.sg-radio input[type="radio"] {
  accent-color: #0d5f54;
  margin: 0;
}
.sg-radio-custom { gap: 8px; }
.sg-radio-active { border-color: #0d5f54; background: #f0faf8; }
.sg-mini-input {
  width: 90px;
  padding: 2px 6px;
  font-size: 12.5px;
  border: 1px solid #e4e7ec;
  border-radius: 5px;
  font-family: inherit;
}
.sg-mini-input:focus {
  outline: none;
  border-color: #0d5f54;
  box-shadow: 0 0 0 2px rgba(13, 95, 84, 0.12);
}

/* Checkboxes — same pill affordance as the radios. */
.sg-check-row {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}
.sg-check {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border: 1px solid #e4e7ec;
  border-radius: 999px;
  font-size: 12.5px;
  color: #1e293b;
  cursor: pointer;
  background: #fff;
}
.sg-check input[type="checkbox"] {
  accent-color: #0d5f54;
  margin: 0;
}
.sg-check input[type="checkbox"]:disabled { cursor: not-allowed; }
.sg-check input[type="checkbox"]:disabled + span { color: #94a3b8; }

/* A nested block of controls under a master toggle. */
.sg-sub {
  margin-top: 14px;
  padding-top: 14px;
  border-top: 1px dashed #e4e7ec;
}
.sg-field-muted { opacity: 0.55; }
.sg-field-muted * { pointer-events: none; }

.sg-toggle-list { display: flex; flex-direction: column; gap: 12px; }
.sg-toggle-row { margin-top: 0; }

/* Toggle switch */
.sg-switch-label {
  display: flex !important;
  align-items: flex-start;
  gap: 12px;
  cursor: pointer;
}
.sg-switch {
  position: relative;
  display: inline-block;
  width: 36px;
  height: 20px;
  flex-shrink: 0;
  margin-top: 1px;
}
.sg-switch input[type="checkbox"] {
  opacity: 0;
  width: 0;
  height: 0;
  position: absolute;
}
.sg-switch-slider {
  position: absolute;
  inset: 0;
  background: #cbd5e1;
  border-radius: 20px;
  transition: background 0.18s;
  cursor: pointer;
}
.sg-switch-slider::before {
  content: '';
  position: absolute;
  height: 16px;
  width: 16px;
  left: 2px;
  top: 2px;
  background: #fff;
  border-radius: 50%;
  transition: transform 0.18s;
  box-shadow: 0 1px 2px rgba(15,23,42,0.12);
}
.sg-switch input:checked + .sg-switch-slider { background: #0d5f54; }
.sg-switch input:checked + .sg-switch-slider::before { transform: translateX(16px); }
.sg-switch-text { display: flex; flex-direction: column; }
.sg-switch-text strong { font-size: 13px; color: #0f172a; font-weight: 600; }
.sg-switch-sub { font-size: 11.5px; color: #64748b; line-height: 1.5; margin-top: 3px; }

/* Save bar */
.sg-savebar {
  position: sticky;
  bottom: 0;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 16px;
  background: rgba(255, 255, 255, 0.96);
  backdrop-filter: blur(8px);
  border: 1px solid #e4e7ec;
  border-radius: 10px;
  margin-top: 24px;
  box-shadow: 0 4px 12px rgba(15,23,42,0.05);
}
.sg-dirty { font-size: 12px; color: #b45309; font-weight: 500; }
.sg-clean { font-size: 12px; color: #64748b; }
.sg-spacer { flex: 1; }
.sg-btn,
.sg-btn-primary {
  padding: 7px 14px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  border: 1px solid #e4e7ec;
  background: #fff;
  color: #1e293b;
}
.sg-btn:hover:not(:disabled) { background: #f1f3f6; }
.sg-btn:disabled,
.sg-btn-primary:disabled { opacity: 0.55; cursor: not-allowed; }
.sg-btn-primary {
  background: #0d5f54;
  border-color: #0d5f54;
  color: #fff;
}
.sg-btn-primary:hover:not(:disabled) { background: #094840; }
</style>
